import { useId, useState, type FormEvent } from "react";
import { ApiError, checkUsername, claimUsername } from "../apiClient.js";
import { AsyncButton } from "../components/AsyncButton.js";
import { DeleteAccountSection } from "../components/DeleteAccountSection.js";
import { UsernameField } from "../components/UsernameField.js";
import { recordSignUpConsent } from "../lib/auth.js";
import { useT, type TranslationKey } from "../lib/i18n/store.js";
import { withInlineNodes } from "../lib/inlineNodes.js";
import { useUsernameAvailability, usernameStatusMessageKey } from "../lib/useUsernameAvailability.js";

/** The claim route's refusals the form explains in its own words (the rest: a generic failure). */
const CLAIM_ERRORS: Record<string, TranslationKey> = {
  username_taken: "username.taken",
  invalid_username: "username.invalid",
  username_reserved: "username.reserved",
  username_rejected: "username.rejected",
  consent_required: "finishSignUp.docsRequired"
};

/**
 * "Finish signing up": shown in place of every page (the legal documents
 * excepted) while the signed-in account has no username of its own — a first
 * sign-in with Google or Discord, or an older account that was handed
 * author<digits>. It asks for what the sign-up form asks for and the provider
 * did not: the username (checked as it is typed, like on sign-up), 18+ and the
 * Terms/Privacy. The consent goes onto the account's metadata as it does at
 * sign-up, then the username is claimed (once: POST /api/profile/username).
 * The account's current generated name counts as free, so it can be kept.
 *
 * Signing out is not the only way out: a visitor who pressed "Continue with
 * Google" by mistake, or does not accept the Terms, can erase the account here
 * (the same confirmation as in the profile editor) — the Privacy Policy
 * promises that right, and nothing else of the app is reachable from this screen.
 */
export function FinishSignUpPage({
  email,
  currentUsername,
  onDone,
  onSignOut,
  onDeleteAccount
}: {
  /** The account's email, so a visitor who signed in with the wrong Google account can tell. */
  email: string;
  /** The generated name the account holds now. */
  currentUsername: string;
  /** Sign-up is over: `claimed` is false when the username was already set (another tab finished it first). */
  onDone: (outcome: { claimed: boolean }) => void;
  onSignOut: () => void;
  /** Erases the account and signs out; rejects with a readable error. */
  onDeleteAccount: () => Promise<void>;
}) {
  const t = useT();
  const idBase = useId();
  const [usernameInput, setUsernameInput] = useState("");
  const usernameCheck = useUsernameAvailability(usernameInput, (name) =>
    currentUsername && name.toLowerCase() === currentUsername.toLowerCase()
      ? Promise.resolve({ available: true, reason: null })
      : checkUsername(name)
  );
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [docsAccepted, setDocsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The documents open in a new tab, so the form keeps what was typed.
  const docLink = (page: "terms" | "privacy", label: string) => (
    <a className="auth-inline-link" href={`/${page}`} target="_blank" rel="noopener">
      {label}
    </a>
  );

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ageConfirmed) {
      setError(t("auth.ageRequired"));
      return;
    }
    if (!docsAccepted) {
      setError(t("finishSignUp.docsRequired"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const settled = await usernameCheck.confirm();
      // A check that could not run does not block: the claim has the final word.
      const problem = settled === "error" ? null : usernameStatusMessageKey(settled);
      if (problem) {
        setError(t(problem));
        return;
      }
      await recordSignUpConsent();
      await claimUsername(usernameCheck.username);
      onDone({ claimed: true });
    } catch (err) {
      // Claimed already (another tab): nothing is left to finish, and the other
      // tab reported the sign-up.
      if (err instanceof ApiError && err.code === "username_already_set") {
        onDone({ claimed: false });
        return;
      }
      const key = err instanceof ApiError && err.code ? CLAIM_ERRORS[err.code] : undefined;
      setError(t(key ?? "finishSignUp.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-page">
      <div className="auth-card finish-signup">
        <h1>{t("finishSignUp.title")}</h1>
        <p className="auth-subtitle">{t("finishSignUp.subtitle")}</p>
        {email && (
          <div className="auth-notice-row finish-signup-account">
            <span className="auth-notice-label">{t("beta.signedInAs")}</span>
            <span className="auth-notice-value">{email}</span>
          </div>
        )}
        <form className="auth-form" onSubmit={(event) => void handleSubmit(event)}>
          <UsernameField
            id={`${idBase}-username`}
            value={usernameInput}
            onChange={(value) => {
              setUsernameInput(value);
              setError(null);
            }}
            status={usernameCheck.status}
          />
          <div className="auth-checks">
            <label className="auth-check">
              <input type="checkbox" checked={ageConfirmed} onChange={(event) => setAgeConfirmed(event.target.checked)} />
              <span>{t("auth.ageConfirm")}</span>
            </label>
            <label className="auth-check">
              <input type="checkbox" checked={docsAccepted} onChange={(event) => setDocsAccepted(event.target.checked)} />
              <span>
                {withInlineNodes(t("auth.acceptDocs"), {
                  terms: docLink("terms", t("auth.termsLink")),
                  privacy: docLink("privacy", t("auth.privacyLink"))
                })}
              </span>
            </label>
          </div>
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
          <AsyncButton type="submit" className="primary-button" busy={busy} loadingLabel={t("auth.working")}>
            {t("finishSignUp.submit")}
          </AsyncButton>
        </form>
        <button type="button" className="auth-switch" onClick={onSignOut}>
          {t("beta.signOut")}
        </button>
        {email && <DeleteAccountSection email={email} onDelete={onDeleteAccount} />}
      </div>
    </section>
  );
}
